<script setup lang="ts">
import { DialogModal, formatNumber, NoteComponent } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { computed, reactive, ref, watchEffect } from 'vue'

import { useUpdateParticipantClickLimitRequest } from '@/api'
import AddPlusIcon from '@/assets/icons/add-plus.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import { notification } from '@/lib/helpers'
import { isIndividualClickLimitPresent } from '@/lib/helpers/override.helpers'
import { maxAmountRule } from '@/lib/rules/common.rules'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  campaignId: number
  participantId: string
  individualClickLimit: number | null
  campaignClickLimit?: number
  campaignPerUserLimit: boolean
  currentClicks: number
}>()
const isOpen = defineModel<boolean>('modelValue', { default: false })
const emits = defineEmits(['onSuccess'])

const state = ref<'present' | 'absent' | 'deletion'>('absent')
const hasIndividualClickLimit = computed(() =>
  isIndividualClickLimitPresent(props.individualClickLimit, props.campaignPerUserLimit, props.campaignClickLimit),
)
const formData = reactive<{ clickLimit: number }>({ clickLimit: 0 })
const ruleFormRef = ref<FormInstance>()

const rules = reactive<FormRules>({
  clickLimit: [
    {
      type: 'number',
      min: 1,
      message: 'Value must be greater than 0',
      trigger: 'blur',
    },
    maxAmountRule,
  ],
})

const { overrideClickLimit, isLoading, error } = useUpdateParticipantClickLimitRequest()

const handleSave = async (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate(async (valid) => {
    if (valid) {
      await overrideClickLimit(props.campaignId, props.participantId, formData.clickLimit)
      isOpen.value = false
      if (!error.value) {
        notification.success('The click limit was updated successfully')
      }
      emits('onSuccess')
    }
  })
}

const handleDelete = async () => {
  await overrideClickLimit(props.campaignId, props.participantId, props.individualClickLimit ? null : 0)
  isOpen.value = false
  if (!error.value) {
    notification.success('The click limit was deleted successfully')
  }
  emits('onSuccess')
}

watchEffect(() => {
  if (hasIndividualClickLimit.value) {
    state.value = 'present'
    formData.clickLimit = props.individualClickLimit || props.campaignClickLimit || 0
  } else {
    state.value = 'absent'
  }
})
</script>
<template>
  <DialogModal v-model="isOpen" @before-close="isOpen = false">
    <template #header>
      <TypographyComponent variant="headline-medium">Edit clicks</TypographyComponent>
    </template>
    <template #default>
      <BoxComponent :mt="24" :mb="24">
        <el-form
          ref="ruleFormRef"
          :model="formData"
          :rules="rules"
          hide-required-asterisk
          :validate-on-rule-change="false"
          :scroll-to-error="true"
          label-position="top"
        >
          <FlexComponent direction="column" :gap="16">
            <BoxComponent :p="16" class="clicks-limit-override-modal__frame">
              <FlexComponent align="center" :gap="16">
                <TypographyComponent variant="title-large" class="clicks-limit-override-modal__label">Current clicks:</TypographyComponent>
                <TypographyComponent variant="body-large">{{ formatNumber(currentClicks) }}</TypographyComponent>
              </FlexComponent>
            </BoxComponent>
            <BoxComponent :p="16" class="clicks-limit-override-modal__frame">
              <FlexComponent v-if="state === 'absent'" :gap="24" align="center">
                <TypographyComponent variant="title-large" class="clicks-limit-override-modal__label">Click limit:</TypographyComponent>
                <FlexComponent :gap="16" align="center">
                  <TypographyComponent variant="body-large">No limit</TypographyComponent>
                  <ButtonComponent variant="outlined" @click="state = 'present'">
                    <template #iconLeft>
                      <AddPlusIcon />
                    </template>
                    Add limit
                  </ButtonComponent>
                </FlexComponent>
              </FlexComponent>
              <FlexComponent v-else-if="state === 'present'" align="center" :gap="8">
                <TypographyComponent variant="title-large" class="clicks-limit-override-modal__label">Click limit:</TypographyComponent>
                <BoxComponent w="100%">
                  <el-form-item prop="clickLimit">
                    <InputComponent
                      v-model.number="formData.clickLimit"
                      type="number"
                      name="amount"
                      class="clicks-limit-override-modal__input"
                    />
                  </el-form-item>
                </BoxComponent>
                <ButtonComponent
                  v-if="hasIndividualClickLimit"
                  is-icon
                  variant="transparent"
                  icon-color="var(--primary)"
                  @click="state = 'deletion'"
                >
                  <DeleteIcon />
                </ButtonComponent>
              </FlexComponent>
              <FlexComponent v-else-if="state === 'deletion'" align="center" :gap="24">
                <TypographyComponent variant="title-large">Are you sure to remove the limit?</TypographyComponent>
                <FlexComponent :gap="8" flex="1" justify="end">
                  <ButtonComponent variant="outlined" @click="state = 'present'">Keep it</ButtonComponent>
                  <ButtonComponent :is-loading="isLoading" variant="danger" @click="handleDelete">Remove</ButtonComponent>
                </FlexComponent>
              </FlexComponent>
              <transition name="slide">
                <BoxComponent :mt="16">
                  <NoteComponent v-if="state === 'absent' && campaignClickLimit">
                    The campaign's click limit is {{ formatNumber(campaignClickLimit) }}.
                  </NoteComponent>
                </BoxComponent>
              </transition>
              <transition name="slide">
                <BoxComponent v-if="formData.clickLimit < currentClicks && formData.clickLimit && state === 'present'" :mt="16">
                  <NoteComponent>
                    If the click limit is set lower than the current clicks, the creator will stop earning from this campaign.
                  </NoteComponent>
                </BoxComponent>
              </transition>
            </BoxComponent>
          </FlexComponent>
        </el-form>
      </BoxComponent>
    </template>
    <template #footer>
      <FlexComponent justify="end" :gap="8">
        <ButtonComponent variant="secondary" @click="isOpen = false">Cancel</ButtonComponent>
        <ButtonComponent :is-loading="isLoading" @click="handleSave(ruleFormRef)">Save</ButtonComponent>
      </FlexComponent>
    </template>
  </DialogModal>
</template>
<style lang="scss" scoped>
.clicks-limit-override-modal__label {
  min-width: 120px;
}
.clicks-limit-override-modal__input {
  flex: 1;
}
.clicks-limit-override-modal__frame {
  border-radius: 16px;
  border: 1px solid rgba(34, 34, 34, 0.08);
}

.slide-enter-active,
.slide-leave-active {
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 100px;
  opacity: 1;
}
</style>
